/*
  Forms for data: data field, data group, pick list. A data field asks its kind of value, its unit, gauge or
  absolute for a pressure, its pick list, size list or kind of project list, one value per case, the columns of a
  table and a written rule (7.10, DEC-1016-93).
*/

import { useState } from "react";

import { useApiQuery } from "../../../api/hooks";
import type { ElementKind, Id, Json, LineChange, LineDraft, LineRow, Phase, ValueKind } from "../../../api/types";
import { Button, IconButton } from "../../../ui";
import { ElementPicker, MultiElementPicker } from "../components/ElementPicker";
import { Check, RadioGroup, Select, TextArea, TextInput } from "../components/fields";
import { isWholeNumber, snake, splitList } from "../lib/text";
import { PHASES, VALUE_KIND_ORDER, VALUE_KIND_WORDS } from "../lib/words";
import { bool, defineSpec, opt, rec, str, type Errors, type FieldsProps, type LineDiff } from "./types";

// ------------------------------------------------------------------------------------------------ units

type UnitValues = { quantityKind: string; ucumCode: string; symbol: string; allowedUnits: string; note: string };

const emptyUnit = (): UnitValues => ({ quantityKind: "", ucumCode: "", symbol: "", allowedUnits: "", note: "" });

function unitFrom(value: unknown): UnitValues {
  const u = rec(value);
  return {
    quantityKind: str(u.quantity_kind),
    ucumCode: str(u.ucum_code),
    symbol: str(u.symbol),
    allowedUnits: Array.isArray(u.allowed_units) ? u.allowed_units.filter((x): x is string => typeof x === "string").join(", ") : "",
    note: str(u.quantity_note),
  };
}

function unitContent(u: UnitValues): Record<string, Json> {
  const code = u.ucumCode.trim();
  const allowed = splitList(u.allowedUnits);
  if (code && !allowed.includes(code)) allowed.unshift(code);
  return {
    ucum_code: code,
    symbol: u.symbol.trim() || code,
    quantity_kind: u.quantityKind.trim(),
    allowed_units: allowed,
    quantity_note: opt(u.note),
  };
}

/**
 * A pressure is what needs the gauge or absolute setting (DEC-1016-67). Like the server, this takes the local name after any
 * prefix ("qk:", "#" or a "/" in an address) and asks whether it ends in "Pressure", so VaporPressure, AbsolutePressure and the plain
 * word "pressure" all ask the question. No kind of pressure is left out by name (UI-18); whether a differential
 * pressure needs the setting is the owner's call, not the framework's.
 */
export function isPressure(quantityKind: string): boolean {
  const local = quantityKind.trim().split(/[:#/]/).pop() ?? "";
  return /pressure$/i.test(local);
}

function validateUnit(u: UnitValues, prefix: string, errors: Errors): void {
  if (!u.quantityKind.trim()) errors[`${prefix}Quantity`] = "Say what is measured.";
  if (!u.ucumCode.trim()) errors[`${prefix}Code`] = "Write the standard unit.";
}

/** A tiny search of one stored library, to fill a box with a term from it. Nothing is looked up online. */
function LibraryFinder({ library, label, onPick }: { library: "QUDT" | "UCUM"; label: string; onPick: (termId: string, termLabel: string) => void }) {
  const [text, setText] = useState("");
  const [asked, setAsked] = useState("");
  const search = useApiQuery("library_search", { q: asked, library }, { enabled: asked !== "", staleTime: 30_000 });
  const hits = search.data?.by_library[library] ?? [];
  return (
    <div className="finder">
      <div className="row">
        <input
          type="search"
          className="field__input"
          aria-label={label}
          placeholder={label}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              setAsked(text.trim());
            }
          }}
        />
        <Button variant="secondary" disabled={!text.trim()} onClick={() => setAsked(text.trim())}>
          Look it up
        </Button>
      </div>
      {asked && search.isError && <p className="form-error">The stored library could not be searched.</p>}
      {asked && !search.isPending && !search.isError && hits.length === 0 && <p className="muted">Nothing found in the stored {library} library.</p>}
      {hits.length > 0 && (
        <ul className="finder__hits">
          {hits.slice(0, 6).map((hit) => (
            <li key={hit.term_id}>
              <Button variant="quiet" onClick={() => onPick(hit.term_id, hit.label)}>
                {hit.label} <span className="muted">({hit.term_id})</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UnitFields({ unit, onChange, errors, prefix }: { unit: UnitValues; onChange: (unit: UnitValues) => void; errors: Errors; prefix: string }) {
  return (
    <fieldset className="field field--group unit-fields">
      <legend className="field__label">Unit details</legend>
      <TextInput
        label="What is measured"
        required
        value={unit.quantityKind}
        error={errors[`${prefix}Quantity`]}
        onChange={(quantityKind) => onChange({ ...unit, quantityKind })}
      />
      <LibraryFinder library="QUDT" label="Look up what is measured" onPick={(termId) => onChange({ ...unit, quantityKind: termId })} />
      <TextInput
        label="Standard unit"
        required
        hint="The one unit used to compare and sort values. Write it as the stored unit library writes it."
        value={unit.ucumCode}
        error={errors[`${prefix}Code`]}
        onChange={(ucumCode) => onChange({ ...unit, ucumCode })}
      />
      <LibraryFinder library="UCUM" label="Look up a unit" onPick={(termId, termLabel) => onChange({ ...unit, ucumCode: termId, symbol: unit.symbol || termLabel })} />
      <TextInput label="Symbol to show" hint="Leave empty to show the standard unit." value={unit.symbol} onChange={(symbol) => onChange({ ...unit, symbol })} />
      <TextInput
        label="Units people may use"
        hint="Separate them with commas. The standard unit is always allowed."
        value={unit.allowedUnits}
        onChange={(allowedUnits) => onChange({ ...unit, allowedUnits })}
      />
    </fieldset>
  );
}

// ------------------------------------------------------------------------------------------------ data field

type ColumnValues = { code: string; title: string; valueKind: ValueKind; unit: UnitValues };
type CaseRow = { name: string; phase: Phase | "" };

type FieldValues = {
  homeKind: "item" | "component_kind" | "capability" | "document_kind" | "shared";
  homeId: string;
  valueKind: ValueKind | "";
  unit: UnitValues;
  pressureReference: "" | "gauge" | "absolute";
  pickListId: string;
  sizeListId: string;
  projectListKind: string;
  perOperatingCase: boolean;
  calculatedFrom: string;
  writtenRule: string;
  columns: ColumnValues[];
  dataGroupId: string;
  rank: string;
  requiredFrom: Phase | "";
  requiredPerCase: CaseRow[];
  valueSuppliedLater: boolean;
  multivalued: boolean;
};

const HOME_OPTIONS: Array<{ value: FieldValues["homeKind"]; label: string }> = [
  { value: "item", label: "An item (its main body)" },
  { value: "component_kind", label: "A component kind" },
  { value: "capability", label: "A capability" },
  { value: "document_kind", label: "A kind of document" },
  { value: "shared", label: "Shared: several items use it for one meaning" },
];

const HOME_ELEMENT_KIND: Record<Exclude<FieldValues["homeKind"], "shared">, ElementKind> = {
  item: "item",
  component_kind: "component_kind",
  capability: "capability",
  document_kind: "document_kind",
};

const HOME_PICK_LABEL: Record<Exclude<FieldValues["homeKind"], "shared">, string> = {
  item: "Which item",
  component_kind: "Which component kind",
  capability: "Which capability",
  document_kind: "Which kind of document",
};

const emptyColumn = (): ColumnValues => ({ code: "", title: "", valueKind: "text", unit: emptyUnit() });

function ColumnRows({ columns, onChange, errors }: { columns: ColumnValues[]; onChange: (columns: ColumnValues[]) => void; errors: Errors }) {
  const update = (index: number, patch: Partial<ColumnValues>) => onChange(columns.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Columns of the table</legend>
      <p className="field__hint muted">Rows of values in named columns. Each column has its own kind of value, and a unit where it is a number.</p>
      {errors.columns && (
        <p role="alert" className="field__error">
          {errors.columns}
        </p>
      )}
      <ol className="column-rows">
        {columns.map((column, index) => (
          <li key={index} className="column-row">
            <div className="row">
              <TextInput label={`Column ${index + 1}: title`} value={column.title} onChange={(title) => update(index, { title, code: column.code || snake(title) })} />
              <TextInput label={`Column ${index + 1}: code`} value={column.code} onChange={(code) => update(index, { code })} />
              <Select
                label={`Column ${index + 1}: kind of value`}
                value={column.valueKind}
                options={VALUE_KIND_ORDER.filter((kind) => kind !== "table").map((kind) => ({ value: kind, label: VALUE_KIND_WORDS[kind] }))}
                onChange={(valueKind) => update(index, { valueKind: valueKind as ValueKind })}
              />
              <IconButton label={`Take out column ${index + 1}`} onClick={() => onChange(columns.filter((_c, i) => i !== index))}>
                ×
              </IconButton>
            </div>
            {column.valueKind === "number" && (
              <UnitFields unit={column.unit} errors={errors} prefix={`column${index}`} onChange={(unit) => update(index, { unit })} />
            )}
          </li>
        ))}
      </ol>
      <div>
        <Button variant="quiet" onClick={() => onChange([...columns, emptyColumn()])}>
          Add a column
        </Button>
      </div>
    </fieldset>
  );
}

function CaseRows({ rows, onChange }: { rows: CaseRow[]; onChange: (rows: CaseRow[]) => void }) {
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Required from, by case (optional)</legend>
      <p className="field__hint muted">Leave empty to require one value from the phase above in any one case.</p>
      <ul className="row-list">
        {rows.map((row, index) => (
          <li key={index} className="row">
            <input
              className="field__input"
              aria-label={`Case ${index + 1}`}
              placeholder="Name of the case"
              value={row.name}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, name: event.target.value } : r)))}
            />
            <select
              className="field__input"
              aria-label={`Required from, case ${index + 1}`}
              value={row.phase}
              onChange={(event) => onChange(rows.map((r, i) => (i === index ? { ...r, phase: event.target.value as Phase | "" } : r)))}
            >
              <option value="">Choose a phase</option>
              {PHASES.map((phase) => (
                <option key={phase} value={phase}>
                  {phase}
                </option>
              ))}
            </select>
            <IconButton label={`Take out case ${index + 1}`} onClick={() => onChange(rows.filter((_r, i) => i !== index))}>
              ×
            </IconButton>
          </li>
        ))}
      </ul>
      <div>
        <Button variant="quiet" onClick={() => onChange([...rows, { name: "", phase: "" }])}>
          Add a case
        </Button>
      </div>
    </fieldset>
  );
}

function ProjectListKindField({ value, error, onChange }: { value: string; error?: string; onChange: (value: string) => void }) {
  const lists = useApiQuery("project_lists", {}, { staleTime: 30_000 });
  const kinds = (lists.data ?? []).map((list) => list.kind);
  return (
    <>
      <TextInput
        label="Kind of project list"
        required
        hint="The field names the kind of list its codes come from. Each project loads its own codes."
        value={value}
        error={error}
        onChange={onChange}
      />
      {kinds.length > 0 && (
        <p className="muted">
          Lists loaded in this discipline: {kinds.map((kind) => (
            <Button key={kind} variant="quiet" onClick={() => onChange(kind)}>
              {kind}
            </Button>
          ))}
        </p>
      )}
      {lists.isSuccess && kinds.length === 0 && <p className="muted">No project list is loaded yet. You can still name the kind now and load the codes later.</p>}
    </>
  );
}

function DataFieldFields({ values, set, errors }: FieldsProps<FieldValues>) {
  const homeIsElement = values.homeKind !== "shared";
  return (
    <>
      <Select
        label="Where does it live"
        required
        value={values.homeKind}
        options={HOME_OPTIONS}
        onChange={(homeKind) => set({ homeKind: homeKind as FieldValues["homeKind"], homeId: "" })}
      />
      {homeIsElement && (
        <ElementPicker
          label={HOME_PICK_LABEL[values.homeKind as Exclude<FieldValues["homeKind"], "shared">]}
          required
          kinds={[HOME_ELEMENT_KIND[values.homeKind as Exclude<FieldValues["homeKind"], "shared">]]}
          value={values.homeId}
          error={errors.homeId}
          onChange={(homeId) => set({ homeId })}
        />
      )}
      <Select
        label="Kind of value"
        required
        value={values.valueKind}
        placeholder="Choose the kind of value"
        options={VALUE_KIND_ORDER.map((kind) => ({ value: kind, label: VALUE_KIND_WORDS[kind] }))}
        error={errors.valueKind}
        onChange={(valueKind) => set({ valueKind: valueKind as ValueKind | "" })}
      />
      {values.valueKind === "number" && (
        <>
          <UnitFields unit={values.unit} errors={errors} prefix="unit" onChange={(unit) => set({ unit })} />
          {isPressure(values.unit.quantityKind) && (
            <RadioGroup
              legend="Gauge or absolute"
              required
              hint="Every pressure carries this setting beside its unit."
              value={values.pressureReference}
              error={errors.pressureReference}
              options={[
                { value: "gauge", label: "Gauge" },
                { value: "absolute", label: "Absolute" },
              ]}
              onChange={(pressureReference) => set({ pressureReference: pressureReference as FieldValues["pressureReference"] })}
            />
          )}
        </>
      )}
      {values.valueKind === "choice" && (
        <ElementPicker label="Its pick list" required kinds={["pick_list"]} value={values.pickListId} error={errors.pickListId} onChange={(pickListId) => set({ pickListId })} />
      )}
      {values.valueKind === "size_designation" && (
        <ElementPicker
          label="Its size list"
          required
          hint="A size designation has no unit. Its list holds each value with its counterpart and the table it comes from."
          kinds={["pick_list"]}
          value={values.sizeListId}
          error={errors.sizeListId}
          onChange={(sizeListId) => set({ sizeListId })}
        />
      )}
      {values.valueKind === "project_code" && <ProjectListKindField value={values.projectListKind} error={errors.projectListKind} onChange={(projectListKind) => set({ projectListKind })} />}
      {values.valueKind === "table" && <ColumnRows columns={values.columns} errors={errors} onChange={(columns) => set({ columns })} />}

      <Check
        label="One value for each operating case"
        hint="Use this when the field is given for cases such as normal and rated. Give the case in the case field, not in the field name."
        checked={values.perOperatingCase}
        onChange={(perOperatingCase) => set({ perOperatingCase })}
      />
      <Check label="The value may be a list of several values" checked={values.multivalued} onChange={(multivalued) => set({ multivalued })} />
      <Check label="The value is supplied later, by someone else" checked={values.valueSuppliedLater} onChange={(valueSuppliedLater) => set({ valueSuppliedLater })} />
      <TextArea
        label="Written rule (optional)"
        hint="Words that say how the value comes about. It is text for people: the app does not calculate or check it."
        rows={2}
        value={values.writtenRule}
        onChange={(writtenRule) => set({ writtenRule })}
      />
      <TextInput label="Calculated from (optional)" hint="Name the lines of a document it is a sum or a result of." value={values.calculatedFrom} onChange={(calculatedFrom) => set({ calculatedFrom })} />
      <ElementPicker label="Data group (optional)" kinds={["data_group"]} value={values.dataGroupId} placeholder="In no group" onChange={(dataGroupId) => set({ dataGroupId })} />
      <TextInput label="Place in its group (optional)" hint="A whole number. Lower numbers come first." value={values.rank} error={errors.rank} onChange={(rank) => set({ rank })} />
      <Select
        label="Required from (optional)"
        hint="The first phase in which a deliverable shows the value. An item can set a different phase for itself."
        value={values.requiredFrom}
        placeholder="No phase set yet"
        options={PHASES.map((phase) => ({ value: phase, label: phase }))}
        onChange={(requiredFrom) => set({ requiredFrom: requiredFrom as Phase | "" })}
      />
      {values.perOperatingCase && <CaseRows rows={values.requiredPerCase} onChange={(requiredPerCase) => set({ requiredPerCase })} />}
      {errors.requiredPerCase && (
        <p role="alert" className="field__error">
          {errors.requiredPerCase}
        </p>
      )}
    </>
  );
}

function columnsFrom(value: unknown): ColumnValues[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const c = rec(entry);
    const kind = str(c.value_kind) as ValueKind;
    return { code: str(c.code), title: str(c.title), valueKind: kind || "text", unit: unitFrom(c.unit) };
  });
}

export const dataFieldSpec = defineSpec<FieldValues>({
  kind: "data_field",
  title: "Data field",
  blurb: "One thing you write down about an item, a component kind, a Connection or a kind of document.",
  nameLabel: "Name of the data field",
  nameHint: "Give the case in the case field, not in the field name.",
  definitionHint: "What the value says.",
  empty: (draft) => ({
    homeKind: draft?.home_kind ?? "item",
    homeId: draft?.home_id ?? "",
    valueKind: "",
    unit: emptyUnit(),
    pressureReference: "",
    pickListId: "",
    sizeListId: "",
    projectListKind: "",
    perOperatingCase: false,
    calculatedFrom: "",
    writtenRule: "",
    columns: [emptyColumn()],
    dataGroupId: "",
    rank: "",
    requiredFrom: "",
    requiredPerCase: [],
    valueSuppliedLater: false,
    multivalued: false,
  }),
  fromElement: (row) => {
    const c = row.content;
    const perCase = rec(c.required_from_default_per_case);
    const home = str(c.home_kind);
    return {
      homeKind: (["item", "component_kind", "capability", "document_kind", "shared"] as const).find((k) => k === home) ?? "shared",
      homeId: str(c.home_id),
      valueKind: (str(c.value_kind) as ValueKind) || "",
      unit: unitFrom(c.unit),
      pressureReference: c.pressure_reference === "gauge" || c.pressure_reference === "absolute" ? c.pressure_reference : "",
      pickListId: str(c.pick_list_id),
      sizeListId: str(c.size_list_id),
      projectListKind: str(c.project_list_kind),
      perOperatingCase: bool(c.per_operating_case),
      calculatedFrom: str(c.calculated_from),
      writtenRule: str(c.written_rule),
      columns: columnsFrom(c.table_columns).length > 0 ? columnsFrom(c.table_columns) : [emptyColumn()],
      dataGroupId: str(c.data_group_id),
      rank: c.rank == null ? "" : String(c.rank),
      requiredFrom: PHASES.find((p) => p === c.required_from_default) ?? "",
      requiredPerCase: Object.entries(perCase).map(([name, phase]) => ({ name, phase: PHASES.find((p) => p === phase) ?? "" })),
      valueSuppliedLater: bool(c.value_supplied_later),
      multivalued: bool(c.multivalued),
    };
  },
  validate: (v) => {
    const errors: Errors = {};
    if (v.homeKind !== "shared" && !v.homeId) errors.homeId = "Choose where the field lives, or make it a shared field.";
    if (!v.valueKind) errors.valueKind = "Choose the kind of value.";
    if (v.valueKind === "number") {
      validateUnit(v.unit, "unit", errors);
      if (isPressure(v.unit.quantityKind) && !v.pressureReference) errors.pressureReference = "Say whether the pressure is gauge or absolute.";
    }
    if (v.valueKind === "choice" && !v.pickListId) errors.pickListId = "Choose the pick list.";
    if (v.valueKind === "size_designation" && !v.sizeListId) errors.sizeListId = "Choose the size list.";
    if (v.valueKind === "project_code" && !v.projectListKind.trim()) errors.projectListKind = "Name the kind of project list.";
    if (v.valueKind === "table") {
      const seen = new Set<string>();
      if (v.columns.length === 0) errors.columns = "A table needs at least one column.";
      v.columns.forEach((column, index) => {
        const code = column.code.trim();
        if (!column.title.trim() || !code) errors.columns = "Give every column a title and a code.";
        else if (seen.has(code)) errors.columns = `Two columns share the code ${code}. Give each column its own code.`;
        seen.add(code);
        if (column.valueKind === "number") validateUnit(column.unit, `column${index}`, errors);
      });
    }
    if (v.rank.trim() && !isWholeNumber(v.rank, 0)) errors.rank = "Write a whole number, or leave it empty.";
    if (v.perOperatingCase && v.requiredPerCase.some((row) => row.name.trim() === "" || row.phase === "")) {
      errors.requiredPerCase = "Give every case a name and a phase, or take the row out.";
    }
    return errors;
  },
  toContent: (v, base) => {
    const perCase: Record<string, Phase> = {};
    if (v.perOperatingCase) for (const row of v.requiredPerCase) if (row.name.trim() && row.phase) perCase[row.name.trim()] = row.phase;
    const kind = v.valueKind as ValueKind;
    return {
      ...base,
      home_kind: v.homeKind,
      home_id: v.homeKind === "shared" ? null : v.homeId,
      value_kind: kind,
      unit: kind === "number" ? unitContent(v.unit) : null,
      pressure_reference: kind === "number" && isPressure(v.unit.quantityKind) ? v.pressureReference || null : null,
      pick_list_id: kind === "choice" ? v.pickListId : null,
      size_list_id: kind === "size_designation" ? v.sizeListId : null,
      project_list_kind: kind === "project_code" ? v.projectListKind.trim() : null,
      per_operating_case: v.perOperatingCase,
      paired_with_id: base?.paired_with_id ?? null,
      calculated_from: opt(v.calculatedFrom),
      written_rule: opt(v.writtenRule),
      table_columns:
        kind === "table"
          ? v.columns.map((column) => ({
              code: column.code.trim(),
              title: column.title.trim(),
              value_kind: column.valueKind,
              unit: column.valueKind === "number" ? unitContent(column.unit) : null,
            }))
          : null,
      data_group_id: v.dataGroupId || null,
      rank: v.rank.trim() ? Number(v.rank) : null,
      required_from_default: v.requiredFrom || null,
      required_from_default_per_case: Object.keys(perCase).length > 0 ? perCase : null,
      value_supplied_later: v.valueSuppliedLater,
      multivalued: v.multivalued,
      framework_field: base ? bool(base.framework_field) : false,
      linkml_extra: base?.linkml_extra ?? {},
    };
  },
  Fields: DataFieldFields,
});

// ------------------------------------------------------------------------------------------------ data group

type GroupValues = { rank: string };

export const dataGroupSpec = defineSpec<GroupValues>({
  kind: "data_group",
  title: "Data group",
  blurb: "A named block of data fields that belong together.",
  nameLabel: "Name of the data group",
  definitionHint: "What the fields of this group have in common.",
  empty: () => ({ rank: "" }),
  fromElement: (row) => ({ rank: row.content.rank == null ? "" : String(row.content.rank) }),
  validate: (v): Errors => (v.rank.trim() && !isWholeNumber(v.rank, 0) ? { rank: "Write a whole number, or leave it empty." } : {}),
  toContent: (v, base) => ({ ...base, rank: v.rank.trim() ? Number(v.rank) : null, linkml_extra: base?.linkml_extra ?? {} }),
  Fields: ({ values, set, errors }) => (
    <TextInput label="Place among the groups (optional)" hint="A whole number. Lower numbers come first." value={values.rank} error={errors.rank} onChange={(rank) => set({ rank })} />
  ),
});

// ------------------------------------------------------------------------------------------------ pick list

type ListKind = "pick_list" | "size_designation" | "exchange_table";

type ValueRow = {
  key: string;
  lineId: Id | null;
  title: string;
  code: string;
  codeTouched: boolean;
  description: string;
  valueSource: "" | "sheet" | "library" | "engineer";
  sizeSystem: string;
  sizeCounterpart: string;
  sizeTable: string;
  libraryCode: string;
  libraryText: string;
};

type PickListValues = {
  listKind: ListKind;
  servesFieldIds: Id[];
  builtFromFieldIds: Id[];
  exchangeLibrary: string;
  exchangeProperty: string;
  exchangePickList: string;
  values: ValueRow[];
};

let rowCounter = 0;
const newRow = (): ValueRow => ({
  key: `value_${Date.now().toString(36)}_${rowCounter++}`,
  lineId: null,
  title: "",
  code: "",
  codeTouched: false,
  description: "",
  valueSource: "",
  sizeSystem: "",
  sizeCounterpart: "",
  sizeTable: "",
  libraryCode: "",
  libraryText: "",
});

function rowFromLine(line: LineRow): ValueRow {
  const c = line.content;
  const size = rec(c.size);
  const exchange = rec(c.exchange);
  const source = str(c.value_source);
  return {
    key: line.id,
    lineId: line.id,
    title: str(c.title),
    code: str(c.code),
    codeTouched: true,
    description: str(c.description),
    valueSource: source === "sheet" || source === "library" || source === "engineer" ? source : "",
    sizeSystem: str(size.system),
    sizeCounterpart: str(size.counterpart_code),
    sizeTable: str(size.source_table),
    libraryCode: str(exchange.library_code),
    libraryText: str(exchange.library_text),
  };
}

function valueContent(row: ValueRow, listKind: ListKind, original?: Record<string, unknown>): Record<string, Json> {
  const content: Record<string, Json> = {
    ...original,
    code: row.code.trim() || snake(row.title),
    title: row.title.trim(),
    description: opt(row.description),
    value_source: row.valueSource || null,
  };
  if (listKind === "size_designation") {
    content.size = { system: row.sizeSystem.trim(), counterpart_code: row.sizeCounterpart.trim(), source_table: row.sizeTable.trim() };
  }
  if (listKind === "exchange_table") {
    content.exchange = { ...rec(original?.exchange), library_code: row.libraryCode.trim(), library_text: row.libraryText.trim() };
  }
  return content;
}

function ValueRows({ values, listKind, onChange, errors }: { values: ValueRow[]; listKind: ListKind; onChange: (rows: ValueRow[]) => void; errors: Errors }) {
  const update = (index: number, patch: Partial<ValueRow>) => onChange(values.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  return (
    <fieldset className="field field--group">
      <legend className="field__label">Values</legend>
      <p className="field__hint muted">Each value is a line of this pick list. A code is made from the title; change it if you need to.</p>
      {errors.values && (
        <p role="alert" className="field__error">
          {errors.values}
        </p>
      )}
      <ol className="value-rows">
        {values.map((row, index) => (
          <li key={row.key} className="value-row">
            <div className="row">
              <TextInput
                label={`Value ${index + 1}: title`}
                value={row.title}
                onChange={(title) => update(index, { title, code: row.codeTouched ? row.code : snake(title) })}
              />
              <TextInput label={`Value ${index + 1}: code`} value={row.code} onChange={(code) => update(index, { code, codeTouched: true })} />
              <IconButton label={`Take out value ${index + 1}`} onClick={() => onChange(values.filter((_r, i) => i !== index))}>
                ×
              </IconButton>
            </div>
            <TextInput label={`Value ${index + 1}: what it means (optional)`} value={row.description} onChange={(description) => update(index, { description })} />
            <Select
              label={`Value ${index + 1}: where it comes from (optional)`}
              value={row.valueSource}
              placeholder="Not said"
              options={[
                { value: "sheet", label: "The sheet prints it" },
                { value: "library", label: "A library gives it" },
                { value: "engineer", label: "The engineer chooses it" },
              ]}
              onChange={(valueSource) => update(index, { valueSource: valueSource as ValueRow["valueSource"] })}
            />
            {listKind === "size_designation" && (
              <div className="row">
                <TextInput label={`Value ${index + 1}: size system`} value={row.sizeSystem} onChange={(sizeSystem) => update(index, { sizeSystem })} />
                <TextInput label={`Value ${index + 1}: counterpart`} value={row.sizeCounterpart} onChange={(sizeCounterpart) => update(index, { sizeCounterpart })} />
                <TextInput label={`Value ${index + 1}: table it comes from`} value={row.sizeTable} onChange={(sizeTable) => update(index, { sizeTable })} />
              </div>
            )}
            {listKind === "exchange_table" && (
              <div className="row">
                <TextInput label={`Value ${index + 1}: library code`} value={row.libraryCode} onChange={(libraryCode) => update(index, { libraryCode })} />
                <TextInput label={`Value ${index + 1}: library text`} value={row.libraryText} onChange={(libraryText) => update(index, { libraryText })} />
              </div>
            )}
          </li>
        ))}
      </ol>
      <div>
        <Button variant="quiet" onClick={() => onChange([...values, newRow()])}>
          Add a value
        </Button>
      </div>
    </fieldset>
  );
}

function PickListFields({ values, set, errors }: FieldsProps<PickListValues>) {
  return (
    <>
      <Select
        label="Kind of list"
        required
        value={values.listKind}
        options={[
          { value: "pick_list", label: "A pick list: the allowed choices of a field" },
          { value: "size_designation", label: "A size designation list: values with their counterpart and table" },
          { value: "exchange_table", label: "An exchange table: values that carry a library's codes" },
        ]}
        onChange={(listKind) => set({ listKind: listKind as ListKind })}
      />
      <MultiElementPicker
        label="Data fields that use it"
        kinds={["data_field"]}
        values={values.servesFieldIds}
        onChange={(servesFieldIds) => set({ servesFieldIds })}
      />
      {values.listKind === "exchange_table" && (
        <fieldset className="field field--group">
          <legend className="field__label">The library it exchanges with</legend>
          <TextInput label="Library" required value={values.exchangeLibrary} error={errors.exchangeLibrary} onChange={(exchangeLibrary) => set({ exchangeLibrary })} />
          <TextInput label="Property in the library" required value={values.exchangeProperty} error={errors.exchangeProperty} onChange={(exchangeProperty) => set({ exchangeProperty })} />
          <TextInput label="Pick list in the library (optional)" value={values.exchangePickList} onChange={(exchangePickList) => set({ exchangePickList })} />
          <MultiElementPicker
            label="Data fields whose answers make the code"
            kinds={["data_field"]}
            values={values.builtFromFieldIds}
            onChange={(builtFromFieldIds) => set({ builtFromFieldIds })}
          />
        </fieldset>
      )}
      <ValueRows values={values.values} listKind={values.listKind} errors={errors} onChange={(rows) => set({ values: rows })} />
    </>
  );
}

function validRows(v: PickListValues): ValueRow[] {
  return v.values;
}

export const pickListSpec = defineSpec<PickListValues>({
  kind: "pick_list",
  title: "Pick list",
  blurb: "The allowed choices of a choice field, or a list of sizes or of a library's codes.",
  nameLabel: "Name of the pick list",
  definitionHint: "What the choices are for.",
  empty: () => ({
    listKind: "pick_list",
    servesFieldIds: [],
    builtFromFieldIds: [],
    exchangeLibrary: "",
    exchangeProperty: "",
    exchangePickList: "",
    values: [newRow()],
  }),
  fromElement: (row, lines) => {
    const c = row.content;
    const exchange = rec(c.exchange);
    const kind = str(c.list_kind);
    return {
      listKind: kind === "size_designation" || kind === "exchange_table" ? kind : "pick_list",
      servesFieldIds: Array.isArray(c.serves_field_ids) ? c.serves_field_ids.filter((x): x is string => typeof x === "string") : [],
      builtFromFieldIds: Array.isArray(exchange.built_from_field_ids) ? exchange.built_from_field_ids.filter((x): x is string => typeof x === "string") : [],
      exchangeLibrary: str(exchange.library),
      exchangeProperty: str(exchange.library_property),
      exchangePickList: str(exchange.library_pick_list),
      values: lines.filter((line) => line.kind === "pick_value" && line.state !== "withdrawn").map(rowFromLine),
    };
  },
  validate: (v, mode) => {
    const errors: Errors = {};
    const rows = validRows(v);
    if (mode === "add" && rows.length === 0) errors.values = "Add at least one value.";
    const seen = new Set<string>();
    for (const row of rows) {
      const code = row.code.trim() || snake(row.title);
      if (!row.title.trim()) {
        errors.values = "Give every value a title, or take the row out.";
        break;
      }
      if (seen.has(code)) {
        errors.values = `Two values share the code ${code}. Give each value its own code.`;
        break;
      }
      seen.add(code);
      if (v.listKind === "size_designation" && (!row.sizeSystem.trim() || !row.sizeCounterpart.trim() || !row.sizeTable.trim())) {
        errors.values = "Every size needs its size system, its counterpart and the table it comes from.";
        break;
      }
      if (v.listKind === "exchange_table" && !row.libraryCode.trim()) {
        errors.values = "Every value of an exchange table needs its library code.";
        break;
      }
    }
    if (v.listKind === "exchange_table") {
      if (!v.exchangeLibrary.trim()) errors.exchangeLibrary = "Name the library.";
      if (!v.exchangeProperty.trim()) errors.exchangeProperty = "Name the property in the library.";
    }
    return errors;
  },
  toContent: (v, base) => ({
    ...base,
    list_kind: v.listKind,
    serves_field_ids: v.servesFieldIds,
    exchange:
      v.listKind === "exchange_table"
        ? {
            library: v.exchangeLibrary.trim(),
            library_property: v.exchangeProperty.trim(),
            library_pick_list: opt(v.exchangePickList),
            built_from_field_ids: v.builtFromFieldIds,
          }
        : null,
    linkml_extra: base?.linkml_extra ?? {},
  }),
  lines: (v): LineDraft[] =>
    v.values.map((row) => ({ client_key: row.key, kind: "pick_value", content: valueContent(row, v.listKind) })),
  diffLines: (v, original): LineDiff => {
    const known = new Map(original.filter((line) => line.kind === "pick_value").map((line) => [line.id, line]));
    const kept = new Set<string>();
    const add: LineDraft[] = [];
    const change: LineChange[] = [];
    for (const row of v.values) {
      const before = row.lineId ? known.get(row.lineId) : undefined;
      if (!row.lineId || !before) {
        add.push({ client_key: row.key, kind: "pick_value", content: valueContent(row, v.listKind) });
        continue;
      }
      kept.add(before.id);
      const content = valueContent(row, v.listKind, before.content);
      if (JSON.stringify(content) !== JSON.stringify(before.content)) change.push({ line_id: before.id, content });
    }
    const remove = [...known.keys()].filter((id) => !kept.has(id));
    return { add, change, remove };
  },
  Fields: PickListFields,
});
